import registryData from "@/config/curricula.json";

/**
 * Static curriculum registry. Will be replaced by an API-backed endpoint later;
 * this module is the swap point — keep all registry access behind these functions.
 */
export interface CurriculumRegistryEntry {
	key: string;
	/**
	 * The curriculum id an organisation stores, and the one everything else keys off: goals
	 * carry it as their `source`, `Goals.store.curriculumItems` keeps only goals whose source
	 * equals it, and the static goal-source file is named after it. It is not the key — some
	 * carry a version (`GO_2023`) — so it is spelled out rather than derived.
	 */
	id: string;
	name: string;
	organisation: string;
	/** ISO country code, matching `Organisation.country` so the two can be compared. */
	country: string;
	/** Emoji flag shown on the country group header; entries sharing a country share it. */
	countryFlag: string;
	locales: string[];
	externalPicker: string | null;
	inlineViewer: boolean;
}

export function getCurriculumRegistry(): CurriculumRegistryEntry[] {
	return registryData as CurriculumRegistryEntry[];
}

/**
 * Org curriculum ids may carry a version suffix (e.g. GO_2023), hence the prefix
 * fallback after the exact case-insensitive match.
 *
 * The prefix must break on a separator: a bare `startsWith(key)` also swallowed ids
 * of unrelated curricula that happen to begin with a key ("GoNavigator" -> "go"),
 * which handed that school a curriculum id with no goal source behind it — the goal
 * selector then filtered every goal away and loaded no structure.
 */
export function findCurriculumRegistryEntry(id: string | null | undefined): CurriculumRegistryEntry | null {
	if (!id) {
		return null;
	}

	const lower = id.toLowerCase();
	const entries = getCurriculumRegistry();

	return entries.find((e) => e.key === lower)
		?? entries.find((e) => lower.startsWith(e.key + '_') || lower.startsWith(e.key + '-'))
		?? null;
}
